"use client";

import { useComponentTranslations, type ServicesTranslationKey } from "@/app/components/LanguageProvider";
import { useLocale } from "@/app/components/LocaleProvider";
import { getPathWithLocale } from "@/lib/i18n";
import Link from "next/link";
import { motion } from "framer-motion";
import PricingCards from "@/app/components/PricingCards";

import PageHero from "@/app/components/PageHero";
import PageContainer from "@/app/components/PageContainer";
import PageSection from "@/app/components/PageSection";
import SectionTitle from "@/app/components/SectionTitle";
import GlassCard from "@/app/components/GlassCard";
import PageCta from "@/app/components/PageCta";
import { CONTACTS } from "@/lib/site-contacts";

const consultingWhatItems = [
  { key: "consultingWhat1" as ServicesTranslationKey },
  { key: "consultingWhat2" as ServicesTranslationKey },
  { key: "consultingWhat3" as ServicesTranslationKey },
  { key: "consultingWhat4" as ServicesTranslationKey },
];

const consultingSteps = [
  { step: "01", titleKey: "consultingStep1Title" as ServicesTranslationKey, descKey: "consultingStep1Desc" as ServicesTranslationKey },
  { step: "02", titleKey: "consultingStep2Title" as ServicesTranslationKey, descKey: "consultingStep2Desc" as ServicesTranslationKey },
  { step: "03", titleKey: "consultingStep3Title" as ServicesTranslationKey, descKey: "consultingStep3Desc" as ServicesTranslationKey },
  { step: "04", titleKey: "consultingStep4Title" as ServicesTranslationKey, descKey: "consultingStep4Desc" as ServicesTranslationKey },
];

const developmentTechItems = [
  { titleKey: "developmentTech1" as ServicesTranslationKey, descKey: "developmentTech1Desc" as ServicesTranslationKey },
  { titleKey: "developmentTech2" as ServicesTranslationKey, descKey: "developmentTech2Desc" as ServicesTranslationKey },
  { titleKey: "developmentTech3" as ServicesTranslationKey, descKey: "developmentTech3Desc" as ServicesTranslationKey },
  { titleKey: "developmentTech4" as ServicesTranslationKey, descKey: "developmentTech4Desc" as ServicesTranslationKey },
  { titleKey: "developmentTech5" as ServicesTranslationKey, descKey: "developmentTech5Desc" as ServicesTranslationKey },
  { titleKey: "developmentTech6" as ServicesTranslationKey, descKey: "developmentTech6Desc" as ServicesTranslationKey },
  { titleKey: "developmentTech7" as ServicesTranslationKey, descKey: "developmentTech7Desc" as ServicesTranslationKey },
  { titleKey: "developmentTech8" as ServicesTranslationKey, descKey: "developmentTech8Desc" as ServicesTranslationKey },
  { titleKey: "developmentTech9" as ServicesTranslationKey, descKey: "developmentTech9Desc" as ServicesTranslationKey },
];

const developmentSteps = [
  { step: "01", titleKey: "developmentStep1Title" as ServicesTranslationKey, descKey: "developmentStep1Desc" as ServicesTranslationKey },
  { step: "02", titleKey: "developmentStep2Title" as ServicesTranslationKey, descKey: "developmentStep2Desc" as ServicesTranslationKey },
  { step: "03", titleKey: "developmentStep3Title" as ServicesTranslationKey, descKey: "developmentStep3Desc" as ServicesTranslationKey },
  { step: "04", titleKey: "developmentStep4Title" as ServicesTranslationKey, descKey: "developmentStep4Desc" as ServicesTranslationKey },
];

const supportWhatItems = [
  { key: "supportWhat1" as ServicesTranslationKey },
  { key: "supportWhat2" as ServicesTranslationKey },
  { key: "supportWhat3" as ServicesTranslationKey },
  { key: "supportWhat4" as ServicesTranslationKey },
];

const supportSteps = [
  { step: "01", titleKey: "supportStep1Title" as ServicesTranslationKey, descKey: "supportStep1Desc" as ServicesTranslationKey },
  { step: "02", titleKey: "supportStep2Title" as ServicesTranslationKey, descKey: "supportStep2Desc" as ServicesTranslationKey },
  { step: "03", titleKey: "supportStep3Title" as ServicesTranslationKey, descKey: "supportStep3Desc" as ServicesTranslationKey },
  { step: "04", titleKey: "supportStep4Title" as ServicesTranslationKey, descKey: "supportStep4Desc" as ServicesTranslationKey },
];

const statsItems = [
  { valueKey: "statsItem1Value" as ServicesTranslationKey, labelKey: "statsItem1Label" as ServicesTranslationKey },
  { valueKey: "statsItem2Value" as ServicesTranslationKey, labelKey: "statsItem2Label" as ServicesTranslationKey },
  { valueKey: "statsItem3Value" as ServicesTranslationKey, labelKey: "statsItem3Label" as ServicesTranslationKey },
  { valueKey: "statsItem4Value" as ServicesTranslationKey, labelKey: "statsItem4Label" as ServicesTranslationKey },
  { valueKey: "statsItem5Value" as ServicesTranslationKey, labelKey: "statsItem5Label" as ServicesTranslationKey },
];

function ProcessTable({ steps, t }: { steps: { step: string; titleKey: ServicesTranslationKey; descKey: ServicesTranslationKey }[]; t: (key: ServicesTranslationKey | string, fallback?: string) => string }) {
  return (
    <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-none pb-2 sm:grid sm:grid-cols-2 lg:grid-cols-4 lg:gap-6 lg:overflow-visible lg:pb-0">
      {steps.map((s) => (
        <div key={s.step} className="snap-start shrink-0 w-[220px] sm:w-auto sm:shrink">
          <div className="text-[11px] tracking-[0.28em] text-white/60 mb-2">
            {s.step}
          </div>
          <div className="text-sm font-semibold text-white mb-1.5">
            {t(s.titleKey, "")}
          </div>
          <div className="text-xs leading-relaxed text-white/60">
            {t(s.descKey, "")}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ServicesClient() {
  const { t } = useComponentTranslations("Services");
  const locale = useLocale();

  return (
    <div className="min-h-screen text-white">
      <PageContainer>
        <PageHero
          title={t('servicesTitle', 'Our Services')}
          description={t('servicesSubtitle', 'Comprehensive IT solutions for your business')}
          badges={
            locale.startsWith("ru")
              ? ["SLA 99.9%", "От 2 недель", "Senior-команда"]
              : ["99.9% SLA", "From 2 weeks", "Senior team"]
          }
          chips={[
            { label: t('consultingTitle', 'Consulting'), href: '#consulting' },
            { label: t('developmentTitle', 'Development'), href: '#development' },
            { label: t('supportTitle', 'Support'), href: '#support' },
            { label: t('pricingTitle', 'Pricing'), href: '#pricing' },
          ]}
          actions={[
            {
              href: getPathWithLocale("/book-call?source=services", locale),
              label: locale.startsWith("ru") ? "Обсудить проект" : "Discuss a project",
              primary: true,
              ctaLocation: "pagehero_services_primary",
            },
            {
              href: CONTACTS.telegramUrl,
              label: locale.startsWith("ru") ? "Связаться в Telegram" : "Message on Telegram",
              ctaLocation: "pagehero_services_secondary",
            },
          ]}
        />
      </PageContainer>

      {/* CONSULTING */}
      <PageSection id="consulting">
          <div className="lg:flex lg:items-start lg:justify-between lg:gap-12 mb-8">
            <div className="max-w-2xl">
              <SectionTitle
            title={t('consultingTitle', 'Consulting')}
            description={t('consultingSubtitle', 'Expert consultation and strategy development for your business.')}
          />

              <p className="text-sm font-semibold text-white/80 mb-3">
                {t('consultingWhatTitle', 'What we do:')}
              </p>
              <ul className="mb-4 space-y-2">
                {consultingWhatItems.map((item, i) => (
                  <li key={i} className="text-sm text-white/70 pl-4 relative">
                    <span className="absolute left-0 top-2.5 h-1 w-1 rounded-full bg-white/30" />
                    {t(item.key, "")}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-white/60 mb-6">
                {t('consultingFor', 'For whom: business owners, top managers, product teams')}
              </p>

              <div className="mb-6">
                <Link
                  href={getPathWithLocale("/services/consulting", locale)}
                  className="text-sm text-white/60 hover:text-white/80 transition-colors"
                >
                  {t('consultingLink', '→ Learn more about consulting')}
                </Link>
              </div>


            </div>
            <div className="hidden lg:flex flex-col items-center shrink-0 self-start mt-4">
              <div className="opacity-30 w-80">
                <svg viewBox="0 0 140 100" preserveAspectRatio="xMidYMid meet" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full aspect-[7/5]">
                  <circle cx="70" cy="30" r="12" stroke="white" strokeWidth="1.5" />
                  <circle cx="30" cy="70" r="8" stroke="white" strokeWidth="1.5" />
                  <circle cx="110" cy="70" r="8" stroke="white" strokeWidth="1.5" />
                  <line x1="58" y1="30" x2="38" y2="62" stroke="white" strokeWidth="1.2" opacity="0.5" />
                  <line x1="82" y1="30" x2="102" y2="62" stroke="white" strokeWidth="1.2" opacity="0.5" />
                  <line x1="38" y1="70" x2="102" y2="70" stroke="white" strokeWidth="1.2" opacity="0.3" strokeDasharray="3 3" />
                  <path d="M120 15 L130 25 L140 10" stroke="white" strokeWidth="1.5" fill="none" />
                </svg>
              </div>

            </div>
          </div>

          <h3 className="text-sm font-semibold text-white/80 mb-4">
            {t('consultingProcessTitle', 'How we work')}
          </h3>
          <ProcessTable steps={consultingSteps} t={t} />
        </PageSection>

        {/* DEVELOPMENT */}
        <PageSection id="development">
          <div className="lg:flex lg:items-start lg:justify-between lg:gap-12 lg:flex-row-reverse mb-8">
            <div className="max-w-2xl">
              <SectionTitle
            title={t('developmentTitle', 'Development')}
            description={t('developmentSubtitle', 'We build tailored software from concept to launch.')}
          />

              <p className="text-sm font-semibold text-white/80 mb-3">
                {t('developmentTechTitle', 'Technology areas:')}
              </p>
              <ul className="mb-4 space-y-2">
                {developmentTechItems.map((item, i) => (
                  <li key={i} className="text-sm text-white/70 pl-4 relative">
                    <span className="absolute left-0 top-2.5 h-1 w-1 rounded-full bg-white/30" />
                    {t(item.titleKey, "")}
                    <span className="text-white/40 ml-1">— {t(item.descKey, "")}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-white/60 mb-6">
                {t('developmentFor', 'For whom: startups, mid-sized businesses, enterprises')}
              </p>

              <div className="mb-6">
                <Link
                  href={getPathWithLocale("/services/development", locale)}
                  className="text-sm text-white/60 hover:text-white/80 transition-colors"
                >
                  {t('developmentLink', '→ Learn more about development')}
                </Link>
              </div>


            </div>
            <div className="hidden lg:flex flex-col items-center shrink-0 self-start mt-4">
              <div className="opacity-30 w-80">
                <svg viewBox="0 0 140 100" preserveAspectRatio="xMidYMid meet" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full aspect-[7/5]">
                  <rect x="15" y="15" width="110" height="70" rx="6" stroke="white" strokeWidth="1.5" />
                  <line x1="15" y1="30" x2="125" y2="30" stroke="white" strokeWidth="1.5" />
                  <circle cx="25" cy="23" r="2.5" stroke="white" strokeWidth="1.2" />
                  <circle cx="33" cy="23" r="2.5" stroke="white" strokeWidth="1.2" />
                  <circle cx="41" cy="23" r="2.5" stroke="white" strokeWidth="1.2" />
                  <text x="22" y="58" fill="white" fontSize="13" opacity="0.6">&lt;/&gt;</text>
                  <text x="70" y="72" fill="white" fontSize="9" opacity="0.3">_ _</text>
                </svg>
              </div>

            </div>
          </div>

          <h3 className="text-sm font-semibold text-white/80 mb-4">
            {t('developmentProcessTitle', 'Development process')}
          </h3>
          <ProcessTable steps={developmentSteps} t={t} />
        </PageSection>

        {/* SUPPORT */}
        <PageSection id="support">
          <div className="lg:flex lg:items-start lg:justify-between lg:gap-12 mb-8">
            <div className="max-w-2xl">
              <SectionTitle
            title={t('supportTitle', 'Support')}
            description={t('supportSubtitle', '24/7 technical support and maintenance for your systems.')}
          />

              <p className="text-sm font-semibold text-white/80 mb-3">
                {t('supportWhatTitle', 'What we do:')}
              </p>
              <ul className="mb-4 space-y-2">
                {supportWhatItems.map((item, i) => (
                  <li key={i} className="text-sm text-white/70 pl-4 relative">
                    <span className="absolute left-0 top-2.5 h-1 w-1 rounded-full bg-white/30" />
                    {t(item.key, "")}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-white/60 mb-6">
                {t('supportFor', 'For whom: companies with existing digital products')}
              </p>

              <div className="mb-6">
                <Link
                  href={getPathWithLocale("/services/support", locale)}
                  className="text-sm text-white/60 hover:text-white/80 transition-colors"
                >
                  {t('supportLink', '→ Learn more about support')}
                </Link>
              </div>


            </div>
            <div className="hidden lg:flex flex-col items-center shrink-0 self-start mt-4">
              <div className="opacity-30 w-80">
                <svg viewBox="0 0 140 100" preserveAspectRatio="xMidYMid meet" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full aspect-[7/5]">
                  <path d="M30 58 C30 28 48 14 70 14 C92 14 110 28 110 58" stroke="white" strokeWidth="1.5" fill="none" />
                  <rect x="22" y="54" width="16" height="18" rx="4" stroke="white" strokeWidth="1.5" fill="none" />
                  <rect x="102" y="54" width="16" height="18" rx="4" stroke="white" strokeWidth="1.5" fill="none" />
                  <line x1="118" y1="64" x2="122" y2="64" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="122" y1="58" x2="122" y2="78" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M118 74 Q122 82 114 86" stroke="white" strokeWidth="1.2" fill="none" strokeLinecap="round" />
                </svg>
              </div>

            </div>
          </div>

          <h3 className="text-sm font-semibold text-white/80 mb-4">
            {t('supportProcessTitle', 'How we work')}
          </h3>
          <ProcessTable steps={supportSteps} t={t} />
        </PageSection>

        {/* PRICING */}
        <div id="pricing" className="scroll-mt-24">
          <PricingCards />
        </div>

        {/* WHY CHOOSE US */}
        <PageSection>
          <SectionTitle title={t('statsTitle', 'Why choose us')} />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            {statsItems.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <GlassCard className="h-full">
                  <div className="text-2xl font-bold leading-tight text-white md:text-3xl">
                    {t(item.valueKey, "")}
                  </div>
                  <div className="mt-1 text-sm leading-normal text-white/60">
                    {t(item.labelKey, "")}
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </PageSection>

        {/* CONTACT */}
        <PageCta
          title={t('contactTitle', 'Ready to discuss your project?')}
          description={t('contactSubtitle', 'Tell us about your task — we will find the best solution.')}
          actions={[
            {
              href: getPathWithLocale("/book-call?source=services", locale),
              label: locale.startsWith("ru") ? "Обсудить проект" : "Discuss a project",
              primary: true,
              ctaLocation: "pagecta_services_primary",
            },
            {
              href: CONTACTS.telegramUrl,
              label: locale.startsWith("ru") ? "Связаться в Telegram" : "Message on Telegram",
              ctaLocation: "pagecta_services_secondary",
            },
          ]}
        >
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/60">
            <a href={`mailto:${CONTACTS.email}`} className="transition-colors hover:text-white">
              {CONTACTS.email}
            </a>
            <a href={`tel:${CONTACTS.phone}`} className="transition-colors hover:text-white">
              {CONTACTS.phoneDisplay}
            </a>
            <a href={CONTACTS.whatsappUrl} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-white">
              WhatsApp {CONTACTS.whatsappDisplay}
            </a>
          </div>
        </PageCta>

    </div>
  );
}
